import {useResizeObserver} from '@mantine/hooks';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import LocalStorage from 'LocalStorage';
import {SidepanelSize, sizeToWidth} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

function useDocumentListener<K extends keyof DocumentEventMap>(
  event: K,
  handler: ((e: DocumentEventMap[K]) => void) | null
): void {
  useEffect(() => {
    if (handler == null) {
      return;
    }
    document.addEventListener(event, handler);
    return () => {
      document.removeEventListener(event, handler);
    };
  }, [event, handler]);
}

const MIN_WIDTH = 320;
const MAX_WIDTH_RATIO = 0.9;
const DEFAULT_FALLBACK_WIDTH = 640;

function getDefaultWidthForSize(size: SidepanelSize): number {
  const w = sizeToWidth[size];
  return typeof w === 'number' ? w : DEFAULT_FALLBACK_WIDTH;
}

interface UseResizableSidepanelOptions {
  storageKey: string;
  defaultPanelSize: SidepanelSize;
}

interface UseResizableSidepanelResult {
  width: number;
  isResizing: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  panelSize: SidepanelSize;
  onPanelSizeChange: (size: SidepanelSize) => void;
}

export function useResizableSidepanel({
  storageKey,
  defaultPanelSize,
}: UseResizableSidepanelOptions): UseResizableSidepanelResult {
  const [panelSize, setPanelSize] = useState<SidepanelSize>(defaultPanelSize);
  const enabled = panelSize !== SidepanelSize.FULL;
  const defaultWidth = getDefaultWidthForSize(defaultPanelSize);
  const [containerRef, containerRect] = useResizeObserver<HTMLDivElement>();

  const [width, setWidth] = useState<number>(() => {
    if (!enabled) {
      return defaultWidth;
    }
    const stored = LocalStorage.getItem(storageKey);
    if (stored != null) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed) && parsed >= MIN_WIDTH) {
        return parsed;
      }
    }
    return defaultWidth;
  });

  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const prevEnabledRef = useRef(enabled);

  useEffect(() => {
    if (enabled && !prevEnabledRef.current) {
      const stored = LocalStorage.getItem(storageKey);
      if (stored != null) {
        const parsed = parseInt(stored, 10);
        if (!Number.isNaN(parsed) && parsed >= MIN_WIDTH) {
          setWidth(parsed);
        }
      }
    }
    prevEnabledRef.current = enabled;
  }, [enabled, storageKey]);

  const getMaxWidth = useCallback((): number => {
    const effectiveWidth = containerRect.width || window.innerWidth;
    return effectiveWidth * MAX_WIDTH_RATIO;
  }, [containerRect.width]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const delta = startXRef.current - e.clientX;
      const maxWidth = getMaxWidth();
      const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidthRef.current + delta));
      setWidth(newWidth);
    },
    [getMaxWidth]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useDocumentListener('mousemove', isResizing ? handleMouseMove : null);
  useDocumentListener('mouseup', isResizing ? handleMouseUp : null);

  useEffect(() => {
    if (!isResizing) {
      return;
    }
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (containerRect.width > 0) {
      const maxWidth = containerRect.width * MAX_WIDTH_RATIO;
      setWidth((prev) => Math.min(prev, maxWidth));
      return;
    }
    const handleWindowResize = (): void => {
      const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
      setWidth((prev) => Math.min(prev, maxWidth));
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [enabled, containerRect.width]);

  useEffect(() => {
    if (!enabled || isResizing) {
      return;
    }
    LocalStorage.setItem(storageKey, String(width));
  }, [width, storageKey, enabled, isResizing]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) {
        return;
      }
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      setIsResizing(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [enabled, width]
  );

  return {width, isResizing, handleMouseDown, containerRef, panelSize, onPanelSizeChange: setPanelSize};
}
